import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from 'react-router-dom';
import { observer, inject } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InvoicePreview from './Invoice-preview';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import AccessControl from "../utils/access_control";
import Checkbox from "./checkbox";
import Modal from './modal';
import ListHeader from "./list-header";
import ListItemDisplay from "./list-item-display";
import { fetch } from '../utils/api-wrapper';
import HoverInfoCard from "./hover-card";
import withRouter from '../components/withRouter';
import Checkmark from "./checkmark";
import Closemark from "./closemark";
import getExpenseCategoryIcon from "../utils/expense-category-icon";
import { canValidateExpense, getReviewableExpenses, isAccountOpenForValidation } from '../pages/review-expense/utils/expense-review-list';

const XpenseList = ({ data, navigate,  expensesAccount, saving, action, setCurrentExpense, setIsNewExpenseCreation, currentExpense, fetchExpensesAccount, approveExpense, disApproveExpense, validateExpense, selectedExpenseChecksums = [], onSelectedExpenseChecksumsChange = () => {}, onRequestExceptions = () => {}, disableRequestExceptionsButton = true, hideRequestExceptionsButton = false, onExpenseAccountSubmitted = () => {} }) => {
    const source = axios.CancelToken.source();
    const [xpensesDeleteSelection, setXpensesDeleteSelection] = useState([]);
    const [xpensesDeleteConfirm, setXpensesDeleteConfirm] = useState(false);
    const expensesComponent = (data?.config?.components || []).find(component => component?.name === "expenses");
    const clientHasControllerView = !!(expensesComponent?.options || []).find(option => (typeof option === "string" ? option : option?.name) === "controller");
    const userHasControllerView = AccessControl.userHasPageOptionAccess(data, "expenses", "controller");
    // Two separate questions: may this person validate, and is this report far
    // enough along to be validated at all (XPEN-3383).
    const canValidate = AccessControl.userCanValidateExpenses(data) && isAccountOpenForValidation(expensesAccount);

    const { account_id } = useParams(); // Access the id parameter from the URL
    const isRequestExceptionSaving = saving && ["request_exception_approval", "validate_exceptions"].includes(action);
    const isExpenseAccountOwner = expensesAccount?.user?.id_user === data.id_user;
    const isAllXpensesSelected = !!(expensesAccount
        && expensesAccount.expenses
        && xpensesDeleteSelection.length > 1
        && xpensesDeleteSelection.length === getReviewableExpenses(expensesAccount.expenses).length
    );
    const toggleAllXpensesSelected = () => {
        const reviewable = getReviewableExpenses(expensesAccount.expenses);
        const nextSelection = xpensesDeleteSelection.length === reviewable.length ? [] : reviewable.map((x) => x.checksum);
        setXpensesDeleteSelection(nextSelection);
        onSelectedExpenseChecksumsChange(nextSelection);
    }

    useEffect(() => {
        setXpensesDeleteSelection(selectedExpenseChecksums || []);
    }, [selectedExpenseChecksums]);
    const isXpenseSelected = (xpens) => {
        return xpensesDeleteSelection.find(x => x === xpens.checksum) !== undefined;
    }
    const deleteXpenses = () => {
        fetch(`/expenses_account/expenses/${account_id}`, "DELETE", { "checksums": xpensesDeleteSelection },
            response => {
                fetchExpensesAccount();
                setXpensesDeleteSelection([]);
                onSelectedExpenseChecksumsChange([]);
                setXpensesDeleteConfirm(false);
            }
            , null, false, source.token);
    }

    const reviewExpense = (expense) => {
        navigate(`/expenses_account/${account_id}/${expense.checksum}/review`);
    }

    const openExpense = (xpens) => {
        if (setIsNewExpenseCreation) setIsNewExpenseCreation(false);
        setCurrentExpense(xpens);
    };

    const approveExceptionIssue = (expense) => new Promise((resolve) => {
        fetch(`/expense_account/exceptions/${account_id}/${expense.checksum}/approve`, "PUT", {},
            (response) => {
                if (response?.data?.expenses_account?.status === "Submitted") {
                    onExpenseAccountSubmitted(response.data.expenses_account);
                } else {
                    fetchExpensesAccount();
                }
                resolve(true);
            },
            () => resolve(false),
            false,
            source.token
        );
    });

    const rejectExceptionIssue = (expense, reason) => new Promise((resolve) => {
        fetch(`/expense_account/exceptions/${account_id}/${expense.checksum}/reject`, "PUT", { reason },
            () => {
                fetchExpensesAccount();
                resolve(true);
            },
            () => resolve(false),
            false,
            source.token
        );
    });
    
    return <><Accordion defaultExpanded className={"rounded-lg shadow-none border mb-3"}
                        sx={{
                            '&:before': {
                                display: 'none',
                            },
                        }}
                        disableGutters
    >
        <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="xpenses-panel-content"
            id="xpenses-panel-header"
            className={"bg-light rounded-lg"}
        >
            <h3>{data.t.read('expenses_account')}</h3>
        </AccordionSummary>
        <AccordionDetails>{data.isMobile ? <>
            <ListHeader
                checkboxDisabled={!expensesAccount || !expensesAccount.expenses}
                isChecked={isAllXpensesSelected}
                onCheckboxChange={toggleAllXpensesSelected}
                hideDeleteButton={expensesAccount?.status !== "Pending"}
                disableDeleteButton={saving || xpensesDeleteSelection.length === 0}
                onDelete={() => setXpensesDeleteConfirm(true)}
                hideAddButton={expensesAccount?.status !== "Pending"}
                addButtonLink={`/expenses_account/${account_id}/add-mobile`}
                extraButtons={!hideRequestExceptionsButton ? [
                    <button
                        key="request-exception"
                        type="button"
                        className="mr-1 pointer btn btn-sm btn-secondary btn-primary-shadow pb-2 pt-2 rounded-lg"
                        disabled={disableRequestExceptionsButton || xpensesDeleteSelection.length === 0}
                        onClick={onRequestExceptions}
                    >
                        <div>
                                {data.t.read("exceptions")}
                            {isRequestExceptionSaving && <FontAwesomeIcon className="ml-2 text-secondary infinite-rotate faded" icon={faSync} />}
                        </div>
                    </button>
                ] : []}
                saving={saving}
                data={data}
            />
            {expensesAccount && getReviewableExpenses(expensesAccount.expenses).map(xpens =>
                <ListItemDisplay
                    key={xpens.checksum}
                    isSelected={isXpenseSelected(xpens)}
                    onSelectedChange={() => {
                        const nextSelection = isXpenseSelected(xpens) ? xpensesDeleteSelection.filter(x => x !== xpens.checksum) : [...xpensesDeleteSelection, xpens.checksum];
                        setXpensesDeleteSelection(nextSelection);
                        onSelectedExpenseChecksumsChange(nextSelection);
                    }}
                >
                    {
                        <div>
                            <FontAwesomeIcon size={"lg"} className={"bg-light p-2 rounded-lg"} icon={getExpenseCategoryIcon(xpens.json?.Category)}/>
                        </div>
                    }
                    {["Pending", "Processing"].includes(xpens.status)
                        ? <FontAwesomeIcon className="ml-2 text-secondary infinite-rotate faded" icon={faSync} />
                        : <div className={"ml-3 d-flex flex-column text-truncate"}>
                            <div className="text-truncate pointer font-weight-bold flex-shrink-1"  onClick={() => openExpense(xpens)}>{xpens.json?.Description ?? xpens.name}</div>
                            <div className="text-muted flex-shrink-1" >{xpens.json?.InvoiceDate}</div>
                        </div>
                    }
                    <div className="flex-1 ml-2">
                        { xpens.ruleViolations?.length > 0 &&
                            <div style={{width: 'fit-content'}}>
                                <HoverInfoCard
                                    custom={true}
                                    position="pos-top-right"
                                    info={<FontAwesomeIcon size="1x" icon={faExclamationTriangle} color={"orange"} type="button" onClick={() => openExpense(xpens)}/>}
                                    additionalInfo={
                                        <div className="alert alert-danger m-0" style={{minWidth:"180px"}} >
                                            <strong>Rule Violations:</strong>
                                            <ul className="mb-0 mt-2">
                                                {xpens.ruleViolations?.map((violation, idx) => (
                                                    <li key={idx}>{violation}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    }
                                />
                            </div>
                        }
                    </div>
                    <div className="text-primary font-weight-bold ml-2">{xpens.json && xpens.json?.BigTotalPayed && `$${xpens.json.BigTotalPayed.toFixed(2)}`}</div>
                    {canValidate && (
                        <div className="ml-2 flex-shrink-0">
                            <Checkmark
                                isChecked={xpens.status === 'Validated'}
                                onChecked={() => validateExpense(xpens)}
                                disabled={!canValidateExpense(xpens.status)}
                                callback={() => fetchExpensesAccount()}
                                iconProps={{
                                    "data-testid": `validate-checkmark-${xpens.checksum}`,
                                    role: "checkbox",
                                    "aria-checked": xpens.status === 'Validated',
                                    "aria-label": data.t.read("validate"),
                                }}
                            />
                        </div>
                    )}
                    {canValidate && (
                        <div className="text-right text-muted flex-shrink-0 ml-2" style={{ width: '96px' }}>
                            {xpens.status ? data.t.read(`status.${xpens.status.toLowerCase()}`) : ''}
                        </div>
                    )}
                </ListItemDisplay>
            )}
            </>
            : <div className="d-flex">
                <div className="col-8">
                    <table className="table table-borderless table-sm">
                        <thead>
                            <tr>
                                <th><Checkbox disabled={!expensesAccount || !expensesAccount.expenses} isChecked={isAllXpensesSelected} onChange={toggleAllXpensesSelected} /></th>
                                <th className="text-center col-2"></th>
                                {AccessControl.userHasPageOptionAccess(data, "expenses", "review") && <th className="text-center col-2">{data.t.read("review")}</th>}
 
                                {clientHasControllerView && <th className="text-center col-3">{data.t.read('expense')} {data.t.read(`name`)}</th>}
                                <th className="text-center col-3">{data.t.read('expense')} {data.t.read(`invoiceDate`)}</th>
                                <th className="text-center col-3">{data.t.read(`invoice.total`)}</th>
                                <th className="text-center col-2">{data.t.read("exceptions")}</th>
                                {canValidate && <th className="text-center col-2 pl-4">{data.t.read("validate")}</th>}
                                {canValidate && <th className="text-center col-2">{data.t.read("validation_status")}</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {expensesAccount && getReviewableExpenses(expensesAccount.expenses).map(
                                xpens => <tr key={xpens.checksum}>
                                    <td><Checkbox isChecked={isXpenseSelected(xpens)} onChange={() => {
                                        const nextSelection = isXpenseSelected(xpens) ? xpensesDeleteSelection.filter(x => x !== xpens.checksum) : [...xpensesDeleteSelection, xpens.checksum];
                                        setXpensesDeleteSelection(nextSelection);
                                        onSelectedExpenseChecksumsChange(nextSelection);
                                    }} /></td>
                                    <td>
                                        <InvoicePreview file_name={xpens.file_name ? xpens.file_name : `${account_id}_${xpens.checksum}.pdf`} currentInvoice={currentExpense} />
                                    </td>
                                    {AccessControl.userHasPageOptionAccess(data, "expenses", "review") && <td className="text-center col-4">
                                        <button disabled={["Pending", "Processing"].includes(xpens.status)} className={`btn ${xpens.json.Reviewed ? 'btn-success' : 'btn-danger'}`} onClick={() => {if (["Pending", "Processing"].includes(xpens.status) !== "Processing") { reviewExpense(xpens); }}}>
                                            {data.t.read("review")}
                                        </button>
                                    </td>}
                                    {
                                    clientHasControllerView && (["Pending", "Processing"].includes(xpens.status) === false ?
                                        <td
                                            className="text-center"
                                            onClick={userHasControllerView ? () => openExpense(xpens) : undefined}
                                            style={userHasControllerView ? { cursor: "pointer", textDecoration: "underline", color: "blue" } : {}}
                                        >
                                            {xpens.json?.Description ?? xpens.name}
                                        </td>
                                        :
                                        <td className="text-center">
                                            <FontAwesomeIcon className="ml-2 text-secondary infinite-rotate faded" icon={faSync} />
                                        </td>)
                                    }
                                    <td className="text-center">
                                        {xpens.json?.InvoiceDate}
                                    </td>
                                    <td className="text-center">
                                        {xpens.json && xpens.json?.BigTotalPayed && `$${xpens.json.BigTotalPayed.toFixed(2)}`}
                                    </td>
                                    <td className="">
                                        {xpens.ruleViolations?.length > 0 && (
                                            <div className="d-flex align-items-center justify-content-center">
                                                <div className="mr-2" onClick={() => openExpense(xpens)}>
                                                    <HoverInfoCard
                                                        custom={true}
                                                        position={"pos-top-right"}
                                                        info={
                                                            <FontAwesomeIcon
                                                                size={"2x"}
                                                                icon={faExclamationTriangle}
                                                                color={"orange"}
                                                                type="button"
                                                            />
                                                        }
                                                        additionalInfo={
                                                            <div className="alert alert-danger m-0" style={{minWidth:"200px"}} >
                                                                <strong>Rule Violations:</strong>
                                                                <ul className="mb-0 mt-2">
                                                                    {xpens.ruleViolations?.map((violation, idx) => (
                                                                        <li key={idx}>{violation}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        }
                                                    />
                                                </div>
                                                {isExpenseAccountOwner && xpens.exception_approval?.status && (
                                                    <span className="mr-2 text-muted text-capitalize">
                                                        {xpens.exception_approval.status.replaceAll("_", " ")}
                                                    </span>
                                                )}
                                                {xpens.exception_approval && !isExpenseAccountOwner && (
                                                    <>
                                                        {(xpens.exception_approval?.status === "approved" || (
                                                            xpens.exception_approval?.status === "pending" &&
                                                            xpens.exception_approval?.approver?.username === data.username &&
                                                            expensesAccount?.status === "Pending_Exception_Approval"
                                                        )) && (
                                                            <Checkmark
                                                                isChecked={xpens.exception_approval?.status === "approved"}
                                                                onChecked={() => approveExceptionIssue(xpens)}
                                                                disabled={xpens.exception_approval?.status !== "pending"}
                                                                iconProps={{
                                                                    "data-toggle": "tooltip",
                                                                    "data-placement": "top",
                                                                    title: "approve the exception",
                                                                }}
                                                            />
                                                        )}
                                                        {(xpens.exception_approval?.status === "rejected" || (
                                                            xpens.exception_approval?.status === "pending" &&
                                                            xpens.exception_approval?.approver?.username === data.username &&
                                                            expensesAccount?.status === "Pending_Exception_Approval"
                                                        )) && (
                                                            <Closemark
                                                                data={data}
                                                                isClosed={xpens.exception_approval?.status === "rejected"}
                                                                onClosed={(reason) => rejectExceptionIssue(xpens, reason)}
                                                                disabled={xpens.exception_approval?.status !== "pending"}
                                                                iconProps={{
                                                                    "data-toggle": "tooltip",
                                                                    "data-placement": "top",
                                                                    title: "deny the exception",
                                                                }}
                                                            />
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    {canValidate && (
                                        <td className="text-center pl-4">
                                            <Checkmark
                                                isChecked={xpens.status === 'Validated'}
                                                onChecked={() => validateExpense(xpens)}
                                                disabled={!canValidateExpense(xpens.status)}
                                                callback={() => fetchExpensesAccount()}
                                                iconProps={{
                                                    "data-testid": `validate-checkmark-${xpens.checksum}`,
                                                    role: "checkbox",
                                                    "aria-checked": xpens.status === 'Validated',
                                                    "aria-label": data.t.read("validate"),
                                                }}
                                            />
                                        </td>
                                    )}
                                    {canValidate && (
                                        <td className="text-center">
                                            {xpens.status ? data.t.read(`status.${xpens.status.toLowerCase()}`) : ''}
                                        </td>
                                    )}
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="col-4 text-right">
                    <button hidden={hideRequestExceptionsButton} type="button" className="mr-1 pointer btn btn-sm btn-secondary btn-primary-shadow pb-2 pt-2" disabled={disableRequestExceptionsButton} onClick={onRequestExceptions}>
                        <div>
                            {data.t.read("request_exception")}
                            {isRequestExceptionSaving && <FontAwesomeIcon className="ml-2 text-secondary infinite-rotate faded" icon={faSync} />}
                        </div>
                    </button>
                    <button hidden={expensesAccount?.status !== "Pending"} type="button" className="mr-1 pointer btn btn-sm btn-secondary btn-primary-shadow pb-2 pt-2" disabled={saving | xpensesDeleteSelection.length === 0} onClick={() => setXpensesDeleteConfirm(true)}>
                        <div>
                            {data.t.read("delete")}
                            {saving && <FontAwesomeIcon className="ml-2 text-secondary infinite-rotate faded" icon={faSync} />}
                        </div>
                    </button>
                    <Link hidden={expensesAccount?.status !== "Pending"} to={`/expenses_account/${account_id}/add`} className="btn btn-sm btn-primary btn-primary-shadow pb-2 pt-2">
                        {data.t.read("add_new")}
                    </Link>
                </div>
            </div>}
        </AccordionDetails>
    </Accordion>
    {
        xpensesDeleteConfirm === true &&
        <Modal title={data.t.read("confirm")} cancelAction={() => setXpensesDeleteConfirm(false)} okAction={() => deleteXpenses()} okText={data.t.read("delete")} cancelText={data.t.read("no")}>
            {data.t.read("delete")} ?
        </Modal>

    }
    </>
}

export default inject('data')(withRouter(observer(XpenseList)));
